import React from "react";
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { spacing } from "../../styles/theme";
import { SpecsInputField } from "./SpecsEditorFields/index";
import { BooleanField } from "./SpecsLayerSpecsControls";
import { GeneratorSideModel } from "./specsModel/GeneratorSideModel";
import { useSpecsEditorContextValue } from "./useSpecsEditor";
import type { VersionModel } from "./specsModel/VersionModel";
import type { GeneratorSideModel as GeneratorSideModelType } from "./specsModel/GeneratorSideModel";
import { SpecsImageEditor } from "./SpecsImageEditor";
import { buildModelImageSourceOptions } from "./specsImageSourceOptions";
import { slugFileSegment } from "./specsImageUpload";

type GeneratorSideAssetKey = "imageUrl" | "highlightUrl";

function createDefaultGlobalSide(name: string) {
  return new GeneratorSideModel().restore({
    name,
    zIndex: 150001,
    zIndexHighlight: 150000,
    imageUrl: "",
    highlightUrl: "",
    alwaysRedraw: false,
  });
}

function getNextSideName(version: VersionModel) {
  const baseName = "front";
  if (!version.generatorSides.some((side) => side.name === baseName)) {
    return baseName;
  }

  let index = 1;
  while (version.generatorSides.some((side) => side.name === `side-${index}`)) {
    index += 1;
  }

  return `side-${index}`;
}

export default function SpecsGlobalPage() {
  const { currentVersion, editor } = useSpecsEditorContextValue();

  if (!currentVersion) {
    return (
      <Typography variant="body2" color="text.secondary">
        Import or create a product before editing global generator sides.
      </Typography>
    );
  }

  const updateSide = (
    side: GeneratorSideModelType,
    patch: Partial<GeneratorSideModelType>
  ) => {
    Object.assign(side, patch);
  };

  const updateSideAsset = (
    side: GeneratorSideModelType,
    key: GeneratorSideAssetKey,
    url: string | null
  ) => {
    if (side.name === "front" && key === "imageUrl") {
      editor.setGlobalMaskFrontUrl(url);
      return;
    }
    if (side.name === "back" && key === "imageUrl") {
      editor.setGlobalMaskBackUrl(url);
      return;
    }
    if (side.name === "front" && key === "highlightUrl") {
      editor.setGlobalHighlightFrontUrl(url);
      return;
    }
    if (side.name === "back" && key === "highlightUrl") {
      editor.setGlobalHighlightBackUrl(url);
      return;
    }

    side[key] = url;
  };
  const getSourceOptions = (sideName: string) =>
    buildModelImageSourceOptions(currentVersion, sideName);
  const getFileNamePrefix = (
    side: GeneratorSideModelType,
    key: GeneratorSideAssetKey
  ) =>
    [
      "global",
      key === "imageUrl" ? "mask" : "highlight",
      slugFileSegment(side.name || "side"),
    ].join("-");
  const getFieldPath = (
    side: GeneratorSideModelType,
    key: GeneratorSideAssetKey
  ) => `generator.sides.${side.name}.${key}`;
  const getDebugPath = (
    side: GeneratorSideModelType,
    key: GeneratorSideAssetKey
  ) => (side._debugPath ? `${side._debugPath}.${key}` : undefined);

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 3 }}
      >
        <Typography variant="h2">Global generator sides</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => {
            currentVersion.generatorSides.push(
              createDefaultGlobalSide(getNextSideName(currentVersion))
            );
          }}
        >
          Add side
        </Button>
      </Stack>

      <Stack spacing={spacing.groupingDesktop}>
        {currentVersion.generatorSides.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No global generator sides.
          </Typography>
        ) : null}
        {currentVersion.generatorSides.map((side, index) => (
          <Paper
            key={`${side.name}:${index}`}
            variant="outlined"
            sx={{
              p: spacing.groupingDesktop,
              borderRadius: "6px",
              minWidth: 0,
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 2 }}
            >
              <Typography variant="subtitle2">
                {side.name || `Side ${index + 1}`}
              </Typography>
              <IconButton
                size="small"
                onClick={() => {
                  currentVersion.generatorSides.splice(index, 1);
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" },
                gap: 2,
              }}
            >
              <SpecsInputField
                label="Side name"
                value={side.name}
                onChange={(event) =>
                  updateSide(side, { name: event.target.value })
                }
              />
              <SpecsInputField
                label="zIndex"
                type="number"
                value={side.zIndex === null ? "" : side.zIndex}
                onChange={(event) =>
                  updateSide(side, {
                    zIndex:
                      event.target.value === ""
                        ? null
                        : Number(event.target.value),
                  })
                }
              />
              <SpecsInputField
                label="zIndexHighlight"
                type="number"
                value={
                  side.zIndexHighlight === null ? "" : side.zIndexHighlight
                }
                onChange={(event) =>
                  updateSide(side, {
                    zIndexHighlight:
                      event.target.value === ""
                        ? null
                        : Number(event.target.value),
                  })
                }
              />
              <BooleanField
                label="Always redraw"
                checked={side.alwaysRedraw}
                onChange={(checked) =>
                  updateSide(side, { alwaysRedraw: checked })
                }
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "minmax(0, 1fr)",
                  md: "repeat(2, minmax(0, 1fr))",
                },
                gap: 2,
                mt: 2,
                minWidth: 0,
              }}
            >
              <SpecsImageEditor
                label="Mask URL"
                value={side.imageUrl || null}
                fileNamePrefix={getFileNamePrefix(side, "imageUrl")}
                sourceOptions={getSourceOptions(side.name)}
                debugPath={getDebugPath(side, "imageUrl")}
                uploadContext={{
                  specType: "product",
                  specName: editor.identity.productName || undefined,
                  fieldPath: getFieldPath(side, "imageUrl"),
                }}
                onChange={(url) => updateSideAsset(side, "imageUrl", url)}
              />
              <SpecsImageEditor
                label="Highlight URL"
                value={side.highlightUrl || null}
                fileNamePrefix={getFileNamePrefix(side, "highlightUrl")}
                sourceOptions={getSourceOptions(side.name)}
                debugPath={getDebugPath(side, "highlightUrl")}
                uploadContext={{
                  specType: "product",
                  specName: editor.identity.productName || undefined,
                  fieldPath: getFieldPath(side, "highlightUrl"),
                }}
                onChange={(url) => updateSideAsset(side, "highlightUrl", url)}
              />
            </Box>
          </Paper>
        ))}
      </Stack>
    </>
  );
}
